/**
 * Call History → call detail ("Recording file").
 *
 * Same structure as the legacy agent-portal Call Recordings detail, rebuilt
 * cleanly:
 *
 *   ┌──────────────────────────── 8/12 ───────────────┬──── 4/12 ─────────────┐
 *   │ Player (parties · split waveform · transport)  │ Agent productivity KPIs│
 *   │ Call Summary ··························· [CDR] │ Customer Matrix        │
 *   │ Tabs: Call Analytics | Call Information |       │ Call Analytics Summary │
 *   │       Hold History | Client Info | Quality Mgmt │  (when available)      │
 *   └─────────────────────────────────────────────────┴────────────────────────┘
 *
 * Times arrive from the API as wall-clock strings already in the report's
 * timezone (`YYYY-MM-DDTHH:MM:SS`), so they are printed as-is — never re-run
 * through a browser-timezone conversion.
 */
import type { JSX, ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity, Annoyed, Check, Contact, Copy, Download, FileJson, FileText, Frown, Gauge,
  Info, ListChecks, Loader2, Meh, PauseCircle, PhoneCall, Smile, Timer, X, AlertCircle, Bot,
} from 'lucide-react'

import { Link } from 'react-router'
import { api } from '../../../api/client.js'
import { formatDateTime as formatInstant } from '../../../utils/formatDate.js'
import { CallRecordingPlayer, hhmmss, type CallRecordingPlayerHandle } from './CallRecordingPlayer.js'
import { QmEvaluationPanel } from './QmEvaluationPanel.js'
import { AiCallPanel, type AiSession } from '../../voice/AiCallPanel.js'

export interface CallHistoryDetailDrawerProps {
  row: Record<string, unknown>
  onClose: () => void
}

interface TranscriptSegment {
  speaker: string
  start: number
  end: number
  text: string
  sentiment?: string
}

interface Span { beginMs: number; endMs: number; durationMs: number }

/* ── Chat-transcript parsing ───────────────────────────────────────────────
 * Manual chat uploads (PDF/txt exports) are stored as the raw text with its
 * line breaks intact. That text still reads flat — the speaker name, message
 * and timestamp land on separate lines, so a reader can't tell who said what.
 * This parser rebuilds the turns: bare name line → message lines → timestamp.
 * Anything it can't recognise stays in the preamble (visitor details…) or the
 * last open turn, so nothing is lost.
 */
interface ChatTurn {
  speaker: string
  text: string
  /** Clock time in seconds-of-day when the export prints one. */
  timeSec: number | null
  system: boolean
}

const CHAT_TIME_RE = /(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)/i
const CHAT_SYSTEM_RE = /\b(forwarded|accepted|declined|transferred|connected)\b.*\b(chat|request|transfer|agent)\b|\bhas (ended|left|joined|started)\b|\bended this chat session\b/i

function clockToSec(h: string, m: string, s: string, meridiem: string): number {
  let hh = Number(h) % 12
  if (meridiem.toUpperCase() === 'PM') hh += 12
  return hh * 3600 + Number(m) * 60 + Number(s)
}

/** A timestamp possibly with a leading date fragment ("24 Jun, 12:38:54 PM"). */
function lineTime(line: string): number | null {
  const m = CHAT_TIME_RE.exec(line)
  return m ? clockToSec(m[1]!, m[2]!, m[3]!, m[4]!) : null
}

/** Strip a trailing timestamp, returning the cleaned text + parsed time. */
function stripTrailingTime(line: string): { text: string; timeSec: number | null } {
  const m = new RegExp(`(.+?)\\s{1,4}${CHAT_TIME_RE.source}\\s*$`, 'i').exec(line)
  if (m) return { text: m[1]!.trim(), timeSec: clockToSec(m[2]!, m[3]!, m[4]!, m[5]!) }
  // Line is only a timestamp (maybe with a date prefix).
  const whole = CHAT_TIME_RE.test(line) && line.replace(CHAT_TIME_RE, '').replace(/^[\d, A-Za-z]+$/, '') === ''
  return whole ? { text: '', timeSec: lineTime(line) } : { text: line, timeSec: null }
}

/** "Kyle L." / "Tim" / "CLLIXIE (bot)" but never a sentence like "night? Thanks" */
function looksLikeName(line: string): boolean {
  const t = line.trim()
  if (t.length < 2 || t.length > 42) return false
  if (/\d|[@_<>{}[\]\\/=+$*#~^|]/.test(t)) return false
  const words = t.split(/\s+/)
  if (words.length > 4) return false
  // Names carry no sentence punctuation anywhere — "night? Thanks" isn't one.
  if (/[?!;:]/.test(t)) return false
  if (/,$/.test(t)) return false
  // Trailing "." only when it's a single-letter initial (Kyle L.).
  if (t.endsWith('.') && !/\b\p{L}\.$/u.test(t)) return false
  return /^[\p{L}]/u.test(t)
}

/** Export header/footer labels that look like "Name: value" but are metadata,
 *  not speakers ("Email: tim@…", "Chat Duration : 00:29:23"…). */
const CHAT_META_NAMES = new Set([
  'chat transcript', 'visitor details', 'visitor', 'email', 'phone', 'website',
  'department', 'operator', 'city', 'state', 'country', 'device', 'browser',
  'operating system', 'os', 'ip', 'ip address', 'referrer', 'page', 'url',
  'location', 'chat ended by', 'average response time', 'chat duration',
  'chat started', 'rating', 'ticket', 'id', 'name', 'chat transcri pt',
])

export function parseChatTranscript(raw: string): { turns: ChatTurn[]; preamble: string } {
  const turns: ChatTurn[] = []
  const preamble: string[] = []
  /** Speaker of the last emitted turn — continuation messages reuse it. */
  let lastSpeaker: string | null = null
  let pendingName: string | null = null
  let pendingTexts: string[] = []
  /** A "24 Jun, 12:38:54" line whose "PM" arrives on the NEXT line. */
  let timePrefix: string | null = null
  /** Last emitted turn — wrapped message tails append here. */
  let lastTurn: ChatTurn | null = null

  // A turn only materializes when a timestamp (or a new message boundary)
  // closes the buffer — export headers can't masquerade as speakers.
  const flushToPreamble = (): void => {
    if (pendingName !== null) preamble.push(pendingName)
    preamble.push(...pendingTexts)
    pendingName = null
    pendingTexts = []
    // A flush is a content boundary — metadata lines must never glue onto
    // the last real message via the wrapped-tail merge.
    lastTurn = null
  }
  const emit = (timeSec: number | null): void => {
    const speaker = pendingName ?? lastSpeaker
    if (speaker === null || pendingTexts.length === 0) {
      flushToPreamble()
      return
    }
    const t: ChatTurn = { speaker, text: pendingTexts.join(' '), timeSec, system: false }
    turns.push(t)
    lastTurn = t
    lastSpeaker = speaker
    pendingName = null
    pendingTexts = []
  }

  for (const rawLine of raw.split(/\r?\n/)) {
    const line = rawLine.trim()
    if (line === '') continue

    // Standalone AM/PM completing a split timestamp.
    if ((line === 'AM' || line === 'PM') && timePrefix !== null) {
      emit(lineTime(`${timePrefix} ${line}`))
      timePrefix = null
      continue
    }
    // Timestamp prefix without meridiem — wait for AM/PM on the next line.
    if (/^\d{1,2}\s+\w+,?\s+\d{1,2}:\d{2}:\d{2}$|^\d{1,2}:\d{2}:\d{2}$/.test(line) && !CHAT_TIME_RE.test(line)) {
      timePrefix = line
      continue
    }

    // Pure timestamp line (with optional date prefix) → close the turn.
    if (/^(?:\d{1,2}\s+\w+,?\s+)?\d{1,2}:\d{2}:\d{2}\s*(?:AM|PM)$/i.test(line)) {
      emit(lineTime(line))
      continue
    }

    // System/service lines between turns.
    if (CHAT_SYSTEM_RE.test(line)) {
      // Buffered message text right before a system line is the tail of the
      // last message — fold it into that turn rather than dropping it.
      if (pendingTexts.length > 0 && lastTurn !== null && !lastTurn.system) {
        lastTurn.text = `${lastTurn.text} ${pendingTexts.join(' ')}`
        pendingTexts = []
        pendingName = null
      } else {
        flushToPreamble()
      }
      const { text, timeSec } = stripTrailingTime(line)
      turns.push({ speaker: 'system', text, timeSec, system: true })
      continue
    }

    // "Label: value" — speaker inline or a metadata field.
    const inline = /^(.{2,42}?):\s*(.+)$/.exec(line)
    if (inline) {
      const name = inline[1]!.trim()
      if (CHAT_META_NAMES.has(name.toLowerCase())) {
        flushToPreamble()
        preamble.push(line)
        continue
      }
      if (looksLikeName(name)) {
        emit(null) // previous buffered turn (timestamp-less formats)
        const { text, timeSec } = stripTrailingTime(inline[2]!)
        const t: ChatTurn = { speaker: name, text, timeSec, system: false }
        turns.push(t)
        lastTurn = t
        lastSpeaker = name
        continue
      }
    }

    // Bare metadata label ("Chat Transcript", "Visitor Details") → preamble.
    if (CHAT_META_NAMES.has(line.toLowerCase())) {
      flushToPreamble()
      preamble.push(line)
      continue
    }

    // Bare name line → next text lines belong to this speaker.
    if (looksLikeName(line)) {
      if (pendingTexts.length > 0) emit(null)
      else if (pendingName !== null) preamble.push(pendingName)
      pendingName = line
      continue
    }

    // Message text (with optional trailing timestamp). A bare line right
    // after a turn whose text is clearly mid-sentence is a wrapped tail of
    // that message (exports wrap long lines AFTER the timestamp column).
    const { text, timeSec } = stripTrailingTime(line)
    if (
      pendingName === null && pendingTexts.length === 0 &&
      lastTurn !== null && !lastTurn.system && !/[.!?…"”]$/.test(lastTurn.text.trim())
    ) {
      lastTurn.text = `${lastTurn.text} ${text}`
      if (lastTurn.timeSec === null && timeSec !== null) lastTurn.timeSec = timeSec
      continue
    }
    pendingTexts.push(text)
    if (timeSec !== null) emit(timeSec)
  }
  emit(null) // trailing buffer without a timestamp
  return { turns, preamble: preamble.join('\n') }
}

/** Stable per-speaker colour so a reader can follow who's talking. */
const SPEAKER_TONES = ['text-blue-700', 'text-emerald-700', 'text-violet-700', 'text-amber-700']
function speakerTone(speaker: string): string {
  let h = 0
  for (let i = 0; i < speaker.length; i++) h = (h * 31 + speaker.charCodeAt(i)) | 0
  return SPEAKER_TONES[Math.abs(h) % SPEAKER_TONES.length]!
}

/**
 * Map AWS's anonymous `spk_N` diarisation labels to real identities.
 *
 *   AGENT / CUSTOMER  → the call's agent / customer directly (Call Analytics).
 *   IVR               → menu phrases ("press", "thank you for calling"…).
 *   agent name in own turns → that label is the agent ("this is Rajni…").
 *   remaining most-talkative → the customer.
 *   anything else           → Speaker N.
 *
 * Display format is always "Role (name)" — e.g. `Agent (Rajni)` /
 * `Customer (Telerain Phone)` — so readers see WHO each line belongs to even
 * when the underlying identity is only a phone number.
 */
function resolveSpeakerLabels(
  segments: TranscriptSegment[],
  ctx: { agentName: string | null; agentExtension: string | null; customerNumber: string | null; clientName: string | null },
): { labels: Map<string, string>; agentSpeakers: Set<string> } {
  const labels = new Map<string, string>()
  const agentSpeakers = new Set<string>()
  const agentLabel = `Agent${ctx.agentName !== null ? ` (${ctx.agentName})` : ctx.agentExtension !== null ? ` (${ctx.agentExtension})` : ''}`
  const customerLabel = `Customer${ctx.clientName !== null ? ` (${ctx.clientName})` : ctx.customerNumber !== null ? ` (${ctx.customerNumber})` : ''}`
  const order: string[] = []
  const talk = new Map<string, number>()
  const texts = new Map<string, string>()
  for (const s of segments) {
    if (!order.includes(s.speaker)) order.push(s.speaker)
    talk.set(s.speaker, (talk.get(s.speaker) ?? 0) + Math.max(0, s.end - s.start))
    texts.set(s.speaker, `${texts.get(s.speaker) ?? ''} ${s.text}`.toLowerCase())
  }
  const remaining = new Set(order)

  for (const s of order) {
    if (s === 'AGENT') { labels.set(s, agentLabel); agentSpeakers.add(s); remaining.delete(s) }
    if (s === 'CUSTOMER') { labels.set(s, customerLabel); remaining.delete(s) }
  }

  // The agent says their own name ("this is Rajni…").
  const agentTokens = (ctx.agentName ?? '').toLowerCase().split(/\s+/).filter((w) => w.length >= 3)
  if (agentTokens.length > 0) {
    for (const s of [...remaining]) {
      const t = texts.get(s) ?? ''
      if (agentTokens.some((tok) => new RegExp(`\\b${tok.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(t))) {
        labels.set(s, agentLabel)
        agentSpeakers.add(s)
        remaining.delete(s)
        break
      }
    }
  }

  // Agent-role phrasing — when the roster name isn't spoken, the speaker who
  // introduces themselves / offers help is the agent side. This must run
  // BEFORE the IVR check: "thank you for calling" is every agent's opening
  // greeting too, and the IVR heuristic would mislabel them.
  const AGENT_PHRASES = ['may i help you', 'how can i help', 'how may i help', 'calling from', 'assist you', 'thank you for contacting', 'my name is']
  let agentPick: string | null = null
  let agentHits = 0
  for (const s of remaining) {
    const t = texts.get(s) ?? ''
    const hits = AGENT_PHRASES.filter((p) => t.includes(p)).length
    if (hits > agentHits) { agentHits = hits; agentPick = s }
  }
  if (agentPick !== null && agentHits > 0) {
    labels.set(agentPick, agentLabel)
    agentSpeakers.add(agentPick)
    remaining.delete(agentPick)
  }

  // IVR — menu-style phrases in that speaker's turns, checked only for
  // speakers that aren't the agent.
  const IVR_PHRASES = ['press ', 'dial ', 'for any ', 'extension', 'please hold', 'your call is important', 'menu']
  for (const s of [...remaining]) {
    const t = texts.get(s) ?? ''
    if (IVR_PHRASES.filter((p) => t.includes(p)).length >= 2) {
      labels.set(s, 'IVR')
      remaining.delete(s)
    }
  }

  // The most-talkative unidentified speaker is the customer.
  if (remaining.size > 0 && (ctx.clientName !== null || ctx.customerNumber !== null)) {
    let best: string | null = null
    let bestSec = -1
    for (const s of remaining) {
      const sec = talk.get(s) ?? 0
      if (sec > bestSec) { bestSec = sec; best = s }
    }
    if (best !== null) { labels.set(best, customerLabel); remaining.delete(best) }
  }

  for (const s of remaining) {
    const n = /(\d+)$/.exec(s)?.[1]
    labels.set(s, n !== undefined ? `Speaker ${Number(n) + 1}` : s)
  }
  return { labels, agentSpeakers }
}

interface CallAnalyticsSummary {
  nonTalkTime: { totalMs: number; instances: Span[] }
  interruptions: { totalCount: number; totalMs: number; byInterrupter: Record<string, Span[]> }
  totalConversationMs: number
  sentiment: Record<string, number>
  talkSpeedWpm: Record<string, number>
  talkTimeMs: Record<string, number>
  totalTalkTimeMs: number
}

interface RecordingDetail {
  id: string
  durationSec: number
  recordingFormat: string
  transcript: string | null
  transcriptStatus?: string | null
  transcriptSegments?: TranscriptSegment[]
  transcriptLanguage?: string | null
  transcriptError?: string | null
  transcriptJobType?: string | null
  transcriptAnalytics?: CallAnalyticsSummary | null
  transcribedAt?: string | null
}

interface CallDetail {
  holds: Array<{ start: string; end: string | null; durationSec: number | null }>
  events: Array<{ event: string | null; fromStatus: string | null; toStatus: string; occurredAt: string; details: unknown }>
  client: { id: string; fullName: string | null; email: string | null; phone: string | null } | null
  /** The customer's number this call was matched on. */
  customerNumber?: string
}

// ─────────────────────────────── formatting ────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Wall-clock `YYYY-MM-DDTHH:MM:SS` → `05 Oct 2026, 13:45:18` without any tz shift. */
function wall(value: unknown): string {
  if (typeof value !== 'string' || value === '') return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(value)
  if (!m) return value
  return `${m[3]} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}, ${m[4]}:${m[5]}:${m[6] ?? '00'}`
}

function text(value: unknown): string {
  if (value === null || value === undefined) return '—'
  const s = String(value).trim()
  return s === '' ? '—' : s
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function secs(value: unknown): string {
  const n = num(value)
  return n === null ? '—' : hhmmss(n)
}

function ms(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : hhmmss(value / 1000)
}

const ACRONYMS = new Set(['ivr', 'did', 'acd', 'sip', 'pstn'])

function titleCase(value: unknown): string {
  const s = text(value)
  if (s === '—') return s
  return s.replace(/[_-]+/g, ' ').toLowerCase()
    .replace(/\b\w+/g, (w) => (ACRONYMS.has(w) ? w.toUpperCase() : w[0]!.toUpperCase() + w.slice(1)))
}

// ─────────────────────────────── primitives ────────────────────────────────

function Card({ title, icon, children, action }: {
  title: string
  icon?: ReactNode
  children: ReactNode
  action?: ReactNode
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          {icon && <span className="text-blue-600">{icon}</span>}
          {title}
        </h3>
        {action}
      </header>
      <div className="px-4 py-2">{children}</div>
    </section>
  )
}

function Field({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-dashed border-slate-100 py-2 last:border-0">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      <span className={`text-right text-sm font-medium text-slate-800 ${mono ? 'break-all font-mono text-xs' : ''}`}>{value}</span>
    </div>
  )
}

function Badge({ children, tone = 'gray' }: { children: ReactNode; tone?: 'gray' | 'green' | 'red' | 'amber' | 'blue' }): JSX.Element {
  const tones = {
    gray: 'bg-slate-100 text-slate-700 ring-slate-200',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    red: 'bg-red-50 text-red-700 ring-red-200',
    amber: 'bg-amber-50 text-amber-700 ring-amber-200',
    blue: 'bg-blue-50 text-blue-700 ring-blue-200',
  }
  return (
    <span className={`inline-flex items-center rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${tones[tone]}`}>
      {children}
    </span>
  )
}

function Empty({ icon, children }: { icon: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-slate-500">
      <span className="text-slate-300">{icon}</span>
      {children}
    </div>
  )
}

// ──────────────────────────────── sentiment ────────────────────────────────

const SENTIMENT = {
  POSITIVE: { Icon: Smile, cls: 'text-emerald-500', label: 'Positive' },
  NEGATIVE: { Icon: Frown, cls: 'text-red-500', label: 'Negative' },
  NEUTRAL: { Icon: Meh, cls: 'text-slate-400', label: 'Neutral' },
  MIXED: { Icon: Annoyed, cls: 'text-amber-500', label: 'Mixed' },
} as const

function SentimentFace({ value }: { value: string | undefined }): JSX.Element {
  const s = value ? SENTIMENT[value as keyof typeof SENTIMENT] : undefined
  if (!s) return <span className="h-6 w-6" />
  return <s.Icon className={`h-6 w-6 flex-shrink-0 ${s.cls}`} aria-label={s.label} />
}

/** Call Analytics overall score (-5 … 5) → label. */
function scoreLabel(score: number | undefined): ReactNode {
  if (score === undefined || !Number.isFinite(score)) return '—'
  const tone = score > 0.5 ? 'green' : score < -0.5 ? 'red' : 'gray'
  const word = score > 0.5 ? 'Positive' : score < -0.5 ? 'Negative' : 'Neutral'
  return <Badge tone={tone}>{word} ({score.toFixed(1)})</Badge>
}

// ──────────────────────────────── tabs ─────────────────────────────────────

type TabId = 'analytics' | 'info' | 'hold' | 'client' | 'qm' | 'ai'

const TABS: Array<{ id: TabId; label: string; Icon: typeof Activity }> = [
  { id: 'analytics', label: 'Call Analytics', Icon: Activity },
  { id: 'info', label: 'Call Information', Icon: PhoneCall },
  { id: 'hold', label: 'Hold History', Icon: PauseCircle },
  { id: 'client', label: 'Client Info', Icon: Contact },
  { id: 'qm', label: 'Quality Management', Icon: ListChecks },
]

/** Shown only when the call had an AI voice agent part (phone or web). */
const AI_TAB = { id: 'ai' as const, label: 'AI Assistant', Icon: Bot }

function TranscriptTab({
  recording, row, agentLabel, clientName, onSeek,
}: {
  recording: RecordingDetail | null
  row: Record<string, unknown>
  agentLabel: string
  clientName: string | null
  onSeek: (seconds: number) => void
}): JSX.Element {
  const [copied, setCopied] = useState(false)
  const status = recording?.transcriptStatus ?? (typeof row['transcriptStatus'] === 'string' ? row['transcriptStatus'] : null)
  const segments = recording?.transcriptSegments ?? []
  const plainText = recording?.transcript ?? null

  const customerNumber = (() => {
    const dir = typeof row['direction'] === 'string' ? row['direction'] : ''
    const n = dir === 'inbound' ? row['fromNumber'] : row['toNumber']
    return typeof n === 'string' && n !== '' ? n : null
  })()
  const speakerLabels = useMemo(
    () => resolveSpeakerLabels(segments, {
      agentName: agentLabel !== 'Agent' ? agentLabel : null,
      agentExtension: typeof row['agentExtension'] === 'string' && row['agentExtension'] !== '' ? row['agentExtension'] : null,
      customerNumber,
      clientName,
    }),
    [segments, agentLabel, customerNumber, clientName, row],
  )
  const who = (speaker: string): string => speakerLabels.labels.get(speaker) ?? speaker
  const isAgentSide = (speaker: string): boolean => speakerLabels.agentSpeakers.has(speaker)

  if (row['hasRecording'] !== true) {
    return <Empty icon={<FileText className="h-8 w-8" />}>No recording for this call, so there is nothing to analyse.</Empty>
  }
  if (segments.length === 0 && !plainText) {
    const pending = status === 'QUEUED' || status === 'IN_PROGRESS'
    const problem = status === 'RETRY' || status === 'FAILED'
    return (
      <Empty icon={pending ? <Loader2 className="h-8 w-8 animate-spin" /> : problem ? <AlertCircle className={`h-8 w-8 ${status === 'FAILED' ? 'text-red-400' : 'text-amber-400'}`} /> : <FileText className="h-8 w-8" />}>
        {pending && 'AWS Transcribe is processing this call — it usually completes a minute or two after the call ends.'}
        {problem && (
          <>
            <span className="font-medium text-slate-700">
              {status === 'FAILED' ? 'Transcription failed.' : 'Transcription could not be started — it will retry automatically.'}
            </span>
            {recording?.transcriptError && (
              <span className={`block max-w-xl rounded-[3px] px-3 py-2 text-xs ${status === 'FAILED' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>
                {recording.transcriptError}
              </span>
            )}
          </>
        )}
        {!pending && !problem && 'No Data Here… Enable Post Call Analytics for this tenant to transcribe recorded calls.'}
      </Empty>
    )
  }

  const plain = segments.length > 0
    ? segments.map((s) => `[${hhmmss(s.start)}] ${who(s.speaker)}: ${s.text}`).join('\n')
    : (plainText ?? '')

  // Manual chat uploads: rebuild speaker turns from the exported layout so
  // the view reads like the original conversation instead of a flat dump.
  const chat =
    segments.length === 0 &&
    plainText !== null &&
    (recording?.transcriptJobType === 'manual_upload')
      ? parseChatTranscript(plainText)
      : null
  const showTurns = chat !== null && chat.turns.length >= 2

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs text-slate-500">
          {recording?.transcriptJobType === 'call_analytics' ? 'AWS Call Analytics'
            : recording?.transcriptJobType === 'manual_upload' ? 'Manual upload'
            : 'AWS Transcribe'}
          {recording?.transcriptLanguage ? ` · ${recording.transcriptLanguage}` : ''}
          {recording?.transcribedAt ? ` · ${formatInstant(recording.transcribedAt)}` : ''}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => { void navigator.clipboard?.writeText(plain).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) }) }}
            className="inline-flex items-center gap-1 rounded-[3px] px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            type="button"
            onClick={() => {
              const u = URL.createObjectURL(new Blob([plain], { type: 'text/plain;charset=utf-8' }))
              const a = document.createElement('a')
              a.href = u
              a.download = `call-${text(row['uniqueId'])}-transcript.txt`
              a.click()
              setTimeout(() => URL.revokeObjectURL(u), 10_000)
            }}
            className="inline-flex items-center gap-1 rounded-[3px] px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </button>
        </div>
      </div>
      {segments.length > 0 ? (
        <ol className="max-h-[32rem] space-y-1 overflow-y-auto pr-1">
          {segments.map((seg, i) => {
            const agent = isAgentSide(seg.speaker)
            return (
              <li key={`${seg.start}-${i}`}>
                <button
                  type="button"
                  onClick={() => onSeek(seg.start)}
                  title="Play from here"
                  className="flex w-full items-start gap-3 rounded-[3px] px-2 py-2 text-left transition-colors hover:bg-blue-50/60"
                >
                  <SentimentFace value={seg.sentiment} />
                  <div className="min-w-0">
                    <p className="text-xs">
                      <span className={`font-semibold ${agent ? 'text-blue-700' : 'text-emerald-700'}`}>{who(seg.speaker)}</span>
                      <span className="ml-1.5 tabular-nums text-slate-400">- {hhmmss(seg.end || seg.start)}</span>
                    </p>
                    <p className="mt-0.5 text-sm leading-relaxed text-slate-800">{seg.text}</p>
                  </div>
                </button>
              </li>
            )
          })}
        </ol>
      ) : showTurns && chat !== null ? (
        <div className="max-h-[32rem] space-y-2.5 overflow-y-auto pr-1">
          {chat.preamble !== '' && (
            <pre className="whitespace-pre-wrap rounded-[3px] border border-slate-100 bg-slate-50 p-2.5 text-[11px] leading-relaxed text-slate-500">
              {chat.preamble}
            </pre>
          )}
          {chat.turns.map((t, i) => (
            t.system ? (
              <p key={i} className="px-1 text-center text-[11px] italic text-slate-400">{t.text}</p>
            ) : (
              <div key={i} className="px-1">
                <p className="text-xs">
                  <span className={`font-semibold ${speakerTone(t.speaker)}`}>{t.speaker}</span>
                  {t.timeSec !== null && (
                    <span className="ml-1.5 tabular-nums text-slate-400">- {hhmmss(t.timeSec)}</span>
                  )}
                </p>
                <p className="mt-0.5 break-words text-sm leading-relaxed text-slate-800">{t.text}</p>
              </div>
            )
          ))}
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{plainText}</p>
      )}
    </div>
  )
}

function InfoTab({ row }: { row: Record<string, unknown> }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="Basic Information" icon={<Info className="h-4 w-4" />}>
        <Field label="Uuid" value={text(row['uniqueId'])} mono />
        <Field label="Source" value={text(row['fromNumber'])} />
        <Field label="Destination" value={text(row['toNumber'])} />
        <Field label="Call type" value={titleCase(row['direction'])} />
        <Field label="Destination type" value={titleCase(row['destinationType'])} />
        <Field label="Agent" value={text(row['agentName'] ?? row['agentExtension'])} />
        <Field label="Start time" value={wall(row['startTime'])} />
        <Field label="Answer time" value={wall(row['answerTime'])} />
        <Field label="End time" value={wall(row['endTime'])} />
        <Field label="Call Duration" value={secs(row['durationSec'])} />
        <Field label="Call Status" value={text(row['callStatus'])} />
      </Card>
      <Card title="Other Information" icon={<Info className="h-4 w-4" />}>
        <Field label="Hangup cause" value={titleCase(row['hangupCause'])} />
        <Field label="Hangup first" value={titleCase(row['hangupParty'])} />
        <Field label="Ringing time" value={secs(row['ringSec'])} />
        <Field label="Hold time" value={secs(row['holdSec'])} />
        <Field label="Talk time" value={secs(row['talkSec'])} />
        <Field label="Queue" value={text(row['queueName'])} />
        <Field label="Queue Start time" value={wall(row['queueEnteredAt'])} />
        <Field label="Queue Answer time" value={wall(row['queueAnsweredAt'])} />
        <Field label="Queue End time" value={wall(row['queueExitedAt'])} />
        <Field label="Queue Answer Status" value={text(row['queueAnswerStatus'])} />
        <Field label="Campaign" value={text(row['campaignName'])} />
        <Field label="Trunk" value={text(row['trunkName'])} />
        <Field label="DID" value={text(row['didName'] ?? row['did'])} />
        <Field label="IVR" value={row['ivrName'] ? `${text(row['ivrName'])}${row['ivrDigits'] ? ` (${text(row['ivrDigits'])})` : ''}` : '—'} />
      </Card>
    </div>
  )
}

function HoldTab({ detail }: { detail: CallDetail | null }): JSX.Element {
  if (!detail) return <Empty icon={<Loader2 className="h-8 w-8 animate-spin" />}>Loading hold history…</Empty>
  if (detail.holds.length === 0) return <Empty icon={<PauseCircle className="h-8 w-8" />}>This call was never put on hold.</Empty>
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
          <th className="py-2 pr-4 font-medium">Hold Start</th>
          <th className="py-2 pr-4 font-medium">Hold End</th>
          <th className="py-2 font-medium text-right">Hold Duration</th>
        </tr>
      </thead>
      <tbody>
        {detail.holds.map((h, i) => (
          <tr key={i} className="border-b border-slate-100 odd:bg-slate-50/60">
            <td className="py-2 pr-4">{wall(h.start)}</td>
            <td className="py-2 pr-4">{wall(h.end)}</td>
            <td className="py-2 text-right tabular-nums">{h.durationSec === null ? 'On hold at hangup' : hhmmss(h.durationSec)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ClientTab({ detail, onCreated }: { detail: CallDetail | null; onCreated: () => void }): JSX.Element {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!detail) return <Empty icon={<Loader2 className="h-8 w-8 animate-spin" />}>Loading client…</Empty>

  if (detail.client) {
    return (
      <div className="max-w-xl">
        <Card title="Client Information" icon={<Contact className="h-4 w-4" />}>
          <Field label="Name" value={text(detail.client.fullName)} />
          <Field label="Phone" value={text(detail.client.phone)} />
          <Field label="Email" value={text(detail.client.email)} />
        </Card>
        <Link
          to={`/agent/customers/${detail.client.id}`}
          className="inline-flex items-center gap-1.5 rounded-[3px] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
        >
          <Contact className="h-3.5 w-3.5" /> View Customer 360
        </Link>
      </div>
    )
  }

  const phone = detail.customerNumber ?? ''
  const save = async (): Promise<void> => {
    setSaving(true)
    setError(null)
    try {
      await api.post('/agent/contacts', {
        name: name.trim(),
        phoneE164: phone.startsWith('+') ? phone : `+${phone.replace(/\D/g, '')}`,
        ...(email.trim() ? { email: email.trim() } : {}),
      })
      setAdding(false)
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the client.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-xl">
      <Card title="No Client Information" icon={<Contact className="h-4 w-4" />}>
        <p className="py-2 text-sm text-slate-600">
          No client record matches <span className="font-mono font-semibold text-slate-800">{phone || '—'}</span>.
        </p>
        {!adding ? (
          phone && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="mb-2 inline-flex items-center gap-1.5 rounded-[3px] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
            >
              <Contact className="h-3.5 w-3.5" /> Add Client
            </button>
          )
        ) : (
          <form
            className="space-y-3 pb-2"
            onSubmit={(e) => { e.preventDefault(); void save() }}
          >
            <label className="block text-xs font-medium text-slate-600">
              Name <span className="text-red-500">*</span>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-[3px] border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
              />
            </label>
            <label className="block text-xs font-medium text-slate-600">
              Phone
              <input value={phone} disabled className="mt-1 block w-full rounded-[3px] border border-slate-200 bg-slate-50 px-2.5 py-1.5 font-mono text-sm text-slate-600" />
            </label>
            <label className="block text-xs font-medium text-slate-600">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-[3px] border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
              />
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button type="submit" disabled={saving || !name.trim()} className="inline-flex items-center gap-1.5 rounded-[3px] bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Client
              </button>
              <button type="button" onClick={() => setAdding(false)} className="rounded-[3px] px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                Cancel
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  )
}

// ──────────────────────────────── CDR dialog ───────────────────────────────

function CdrDialog({ row, detail, onClose }: {
  row: Record<string, unknown>
  detail: CallDetail | null
  onClose: () => void
}): JSX.Element {
  const json = useMemo(() => JSON.stringify({ call: row, events: detail?.events ?? [] }, null, 2), [row, detail])
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-6" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-[3px] bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900"><FileJson className="h-4 w-4 text-purple-600" /> Call Detail Records</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-[3px] p-1 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        {!detail ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> loading CDR data…</div>
        ) : (
          <pre className="flex-1 overflow-auto bg-slate-950 p-4 font-mono text-[11px] leading-relaxed text-slate-100">{json}</pre>
        )}
      </div>
    </div>
  )
}

// ──────────────────────────────── main ─────────────────────────────────────

export function CallHistoryDetailDrawer({ row, onClose }: CallHistoryDetailDrawerProps): JSX.Element {
  const previousFocus = useRef<HTMLElement | null>(null)
  const playerRef = useRef<CallRecordingPlayerHandle | null>(null)
  const [tab, setTab] = useState<TabId>('analytics')
  const [showCdr, setShowCdr] = useState(false)
  const [recording, setRecording] = useState<RecordingDetail | null>(null)
  const [detail, setDetail] = useState<CallDetail | null>(null)

  const callId = typeof row['id'] === 'string' ? row['id'] : null
  const recordingId = typeof row['recordingId'] === 'string' ? row['recordingId'] : null
  const hasRecording = row['hasRecording'] === true && recordingId !== null
  const timezone = typeof row['timezone'] === 'string' ? row['timezone'] : 'UTC'
  const agentLabel = text(row['agentName'] ?? row['agentExtension']) === '—' ? 'Agent' : text(row['agentName'] ?? row['agentExtension'])

  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement
    const onKeyDown = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      previousFocus.current?.focus()
    }
  }, [onClose])

  // Live: re-read the recording while its transcript is still on its way, so
  // Queued → Processing → transcript appears without reloading the page.
  useEffect(() => {
    if (!recordingId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let polls = 0
    const load = (): void => {
      void api.get<RecordingDetail>(`/recordings/${recordingId}`)
        .then((d) => {
          if (cancelled) return
          setRecording(d)
          const s = d.transcriptStatus ?? null
          const done = s === 'COMPLETED' || s === 'FAILED'
          // A null status may mean analytics is off — give it ~3 min, not forever.
          if (!done && (s !== null || polls < 36)) {
            polls++
            timer = setTimeout(load, 5_000)
          }
        })
        .catch(() => { /* the player surfaces playback errors itself */ })
    }
    load()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [recordingId])

  // The AI voice agent's part of this call, if any (null = no AI part).
  const [aiSession, setAiSession] = useState<AiSession | null>(null)
  useEffect(() => {
    if (!callId) return
    let cancelled = false
    void api.get<{ item: AiSession | null }>(`/calls/${callId}/ai-session`)
      .then((r) => { if (!cancelled) setAiSession(r.item) })
      .catch(() => { /* no AI part, or no permission — the tab just stays hidden */ })
    return () => { cancelled = true }
  }, [callId])

  const [detailVersion, setDetailVersion] = useState(0)
  useEffect(() => {
    if (!callId) return
    let cancelled = false
    void api.get<CallDetail>(`/reports/calls/history/${callId}?timezone=${encodeURIComponent(timezone)}`)
      .then((d) => { if (!cancelled) setDetail(d) })
      .catch(() => { if (!cancelled) setDetail({ holds: [], events: [], client: null }) })
    return () => { cancelled = true }
  }, [callId, timezone, detailVersion])

  const analytics = recording?.transcriptAnalytics ?? null
  const inbound = row['direction'] === 'inbound'
  const status = text(row['callStatus'])
  const statusTone = status === 'Answered' ? 'green' : status === 'Abandoned' ? 'red' : 'amber'
  const fileName = `call-${text(row['uniqueId'])}.${recording?.recordingFormat === 'mp3' ? 'mp3' : 'wav'}`
  const holdCount = detail?.holds.length

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden="true" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Recording file"
        className="fixed right-0 top-0 z-50 flex h-screen w-[calc(100vw-264px)] min-w-[56rem] flex-col bg-slate-50 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3.5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-[3px] bg-blue-600 text-white"><PhoneCall className="h-4 w-4" /></span>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Recording file</h2>
              <p className="font-mono text-xs text-slate-500">{text(row['uniqueId'])}</p>
            </div>
            <Badge tone="blue">{titleCase(row['direction'])}</Badge>
            <Badge tone={statusTone}>{status}</Badge>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500">{wall(row['startTime'])} <span className="text-slate-400">({timezone})</span></span>
            <button type="button" aria-label="Close" onClick={onClose} className="rounded-[3px] p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Left — 8/12 */}
            <div className="col-span-12 space-y-5 xl:col-span-8">
              {hasRecording ? (
                <CallRecordingPlayer
                  ref={playerRef}
                  recordingId={recordingId}
                  source={text(row['fromNumber'])}
                  destination={text(row['toNumber'])}
                  channelLabels={inbound ? ['Customer', 'Agent'] : ['Agent', 'Customer']}
                  fileName={fileName}
                />
              ) : (
                <section className="rounded-[3px] border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                  No recording is stored for this call.
                </section>
              )}

              {/* Call Summary + CDR */}
              <section className="flex flex-wrap items-center justify-between gap-3 rounded-[3px] border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <span className="text-sm font-semibold text-slate-800">Call Summary</span>
                  <Summary label="Agent" value={agentLabel === 'Agent' ? '—' : agentLabel} />
                  <Summary label="Queue" value={text(row['queueName'])} />
                  <Summary label="Talk" value={secs(row['talkSec'])} />
                  <Summary label="Disposition" value={text(row['dispositionName'])} />
                  <Summary label="Hangup" value={titleCase(row['hangupCause'])} />
                </div>
                <button
                  type="button"
                  onClick={() => setShowCdr(true)}
                  className="inline-flex items-center gap-1.5 rounded-[3px] bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-purple-700"
                >
                  <FileJson className="h-3.5 w-3.5" /> CDR
                </button>
              </section>

              {/* Tabs */}
              <section className="overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm">
                <nav className="flex overflow-x-auto border-b border-slate-200" role="tablist">
                  {(aiSession ? [AI_TAB, ...TABS] : TABS).map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={tab === id}
                      onClick={() => setTab(id)}
                      className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                        tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      <Icon className="h-4 w-4" /> {label}
                    </button>
                  ))}
                </nav>
                <div className="p-4" role="tabpanel">
                  {tab === 'analytics' && (
                    <TranscriptTab recording={recording} row={row} agentLabel={agentLabel} clientName={detail?.client?.fullName ?? null} onSeek={(s) => playerRef.current?.playFrom(s)} />
                  )}
                  {tab === 'ai' && aiSession && <AiCallPanel session={aiSession} showRecording={false} />}
                  {tab === 'info' && <InfoTab row={row} />}
                  {tab === 'hold' && <HoldTab detail={detail} />}
                  {tab === 'client' && <ClientTab detail={detail} onCreated={() => setDetailVersion((v) => v + 1)} />}
                  {tab === 'qm' && callId && (
                    <QmEvaluationPanel callId={callId} hasTranscript={(recording?.transcriptStatus ?? row['transcriptStatus']) === 'COMPLETED'} />
                  )}
                </div>
              </section>
            </div>

            {/* Right — 4/12 */}
            <div className="col-span-12 space-y-5 xl:col-span-4">
              <Card title="Agent productivity KPIs" icon={<Gauge className="h-4 w-4" />}>
                <Field label="Handling Time" value={secs(row['handleSec'])} />
                {/* ACD calls have no separate ring phase; their wait is the queue wait. */}
                <Field label="Speed of Answer" value={secs(row['ringSec'] ?? row['queueWaitSec'])} />
                <Field label="Total Hold time" value={secs(row['holdSec'])} />
                <Field label="Hold Count" value={holdCount === undefined ? '—' : holdCount} />
                <Field label="Disposition Time" value={secs(row['wrapUpSec'])} />
              </Card>
              <Card title="Customer Matrix" icon={<Timer className="h-4 w-4" />}>
                <Field label="IVR time" value={secs(row['ivrSec'])} />
                <Field label="Queue Time" value={secs(row['queueWaitSec'])} />
                <Field label="Hold Time" value={secs(row['holdSec'])} />
                <Field label="Call Duration" value={secs(row['durationSec'])} />
              </Card>

              {analytics && (
                <>
                  <h3 className="pt-1 text-sm font-semibold text-slate-800">Call Analytics Summary</h3>
                  <Card title="Non Talktime">
                    <Field label="Begin Offset" value={ms(analytics.nonTalkTime.instances[0]?.beginMs)} />
                    <Field label="Duration" value={ms(analytics.nonTalkTime.totalMs)} />
                    <Field label="End Offset" value={ms(analytics.nonTalkTime.instances[0]?.endMs)} />
                  </Card>
                  <Card title="Interruptions">
                    <Field label="Total Count" value={analytics.interruptions.totalCount} />
                    {Object.entries(analytics.interruptions.byInterrupter).map(([role, list]) => (
                      <Field key={role} label={`By ${role === 'AGENT' ? 'Agent' : 'Customer'}`} value={list.length} />
                    ))}
                    <Field label="Total Time" value={ms(analytics.interruptions.totalMs)} />
                  </Card>
                  <Card title="Total Conversation Time">
                    <Field label="Duration" value={ms(analytics.totalConversationMs)} />
                  </Card>
                  <Card title="Sentiment">
                    {analytics.sentiment['AGENT'] !== undefined && (
                      <Field label="Agent Overall Sentiment" value={scoreLabel(analytics.sentiment['AGENT'])} />
                    )}
                    <Field label="Customer Overall Sentiment" value={scoreLabel(analytics.sentiment['CUSTOMER'])} />
                  </Card>
                  <Card title="Talk Speed">
                    <Field label="Agent Average Words/Minute" value={analytics.talkSpeedWpm['AGENT'] ?? '—'} />
                    <Field label="Customer Average Words/Minute" value={analytics.talkSpeedWpm['CUSTOMER'] ?? '—'} />
                  </Card>
                  <Card title="Call Analytics Talktime">
                    <Field label="Agent Talktime" value={ms(analytics.talkTimeMs['AGENT'])} />
                    <Field label="Customer Talktime" value={ms(analytics.talkTimeMs['CUSTOMER'])} />
                    <Field label="Total Talktime" value={ms(analytics.totalTalkTimeMs)} />
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      {showCdr && <CdrDialog row={row} detail={detail} onClose={() => setShowCdr(false)} />}
    </>
  )
}

function Summary({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="text-xs text-slate-500">
      {label}: <span className="font-semibold text-slate-800">{value}</span>
    </span>
  )
}
